import { Link } from '@tanstack/react-router';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { QcTemplate } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { ConfirmDeleteDialog } from '../components/confirm-delete-dialog';
import { useDeleteQcTemplate } from '../hooks/use-qc-template-mutations';
import { useQcTemplates } from '../hooks/use-qc-templates';

function QcChecklists() {
  const { t } = useTranslation(['workflows', 'common']);
  const format = useFormat();
  const store = useStore();
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });
  const query = useQcTemplates(store.id);
  const remove = useDeleteQcTemplate(store.id);
  const [deleting, setDeleting] = useState<QcTemplate | null>(null);
  const checklists = query.data ?? [];

  return (
    <PageContent>
      <PageHeader
        title={t('qc.title')}
        description={t('qc.description', { store: store.name })}
        backTo={{ to: '/s/$storeId/settings/workflows', params: { storeId: store.id } }}
        backLabel={t('qc.back')}
        actions={
          editable ? (
            <Button asChild data-tour="qc.list.new">
              <Link to="/s/$storeId/settings/qc-checklists/new" params={{ storeId: store.id }}>
                <Plus aria-hidden />
                {t('qc.new')}
              </Link>
            </Button>
          ) : null
        }
      />
      <ReadOnlyBanner reason={reason} />
      <FormSection title={t('qc.listLabel')} className="max-w-4xl">
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.isPending ? (
          <Skeleton className="h-10 w-full" />
        ) : checklists.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-tour="qc.list.empty">
            {t('qc.empty')}
          </p>
        ) : (
          <div className="-mx-6" data-tour="qc.list.table">
            <Table aria-label={t('qc.listLabel')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.name')}</TableHead>
                  <TableHead>{t('qc.fieldsTitle')}</TableHead>
                  <TableHead>{t('columns.updated')}</TableHead>
                  <TableHead className="pr-6 text-right">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checklists.map((checklist) => (
                  <TableRow key={checklist.id} aria-label={checklist.name}>
                    <TableCell className="pl-6 font-medium">{checklist.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {t('qc.fieldCount', { count: checklist.fields.length })}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <span title={format.utc(checklist.updatedAt)}>
                        {format.relative(checklist.updatedAt)}
                      </span>
                    </TableCell>
                    <TableCell className="pr-6 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="outline" size="sm">
                          <Link
                            to="/s/$storeId/settings/qc-checklists/$checklistId"
                            params={{ storeId: store.id, checklistId: checklist.id }}
                            aria-label={t('qc.edit', { name: checklist.name })}
                          >
                            <Pencil aria-hidden />
                            {t('common:actions.edit')}
                          </Link>
                        </Button>
                        {editable ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('qc.deleteLabel', { name: checklist.name })}
                            onClick={() => {
                              remove.reset();
                              setDeleting(checklist);
                            }}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </FormSection>
      <ConfirmDeleteDialog
        open={deleting !== null}
        title={t('qc.delete.title', { name: deleting?.name ?? '' })}
        description={t('qc.delete.description')}
        confirmLabel={t('qc.delete.confirm')}
        inUseMessage={t('qc.delete.inUse')}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function QcChecklistsPage() {
  return (
    <FeatureErrorBoundary name="workflows.qc.list">
      <QcChecklists />
    </FeatureErrorBoundary>
  );
}
